import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDealflowDebug } from "../api/adminApi";
import { Section, BarList, fmtDate } from "../components/admin/AdminUI";
import { useAuth } from "../auth/AuthContext";

const FUNNEL_KEYS = [
  "stage_0_input",
  "stage_1_active",
  "stage_2_country_match",
  "stage_3_image_health_gate",
  "stage_4_user_pref_match",
  "stage_5_dedupe",
  "stage_6_after_cursor",
  "stage_7_returned",
];

const EXCLUSION_KEYS = [
  "missing_url", "missing_price", "missing_image",
  "image_health_dead", "image_health_unchecked",
  "country_mismatch", "price_out_of_range", "year_out_of_range",
  "km_out_of_range", "make_mismatch",
];

function FunnelBar({ label, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-44 text-zinc-400 truncate font-mono">{label}</div>
      <div className="flex-1 h-3 rounded bg-zinc-900 overflow-hidden">
        <div className="h-full bg-emerald-500/60" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-24 text-right tabular-nums text-zinc-300">
        {value.toLocaleString("de-AT")}
        <span className="text-zinc-500"> ({Math.round(pct)}%)</span>
      </div>
    </div>
  );
}

export default function AdminDealflowDebugPage() {
  const { user } = useAuth();
  const [vertical, setVertical] = useState("auto");
  const [userUlid, setUserUlid] = useState(user?.ulid || "");
  const [includeDead, setIncludeDead] = useState(false);
  const [includeUnchecked, setIncludeUnchecked] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "dealflow-debug", vertical, userUlid, includeDead, includeUnchecked, refreshKey],
    queryFn: () => getDealflowDebug(vertical, {
      include_dead: includeDead,
      include_unchecked: includeUnchecked,
      sample_size: 10,
      ...(userUlid ? { user_ulid: userUlid } : {}),
    }),
    refetchOnMount: true,
  });

  const funnel = data?.funnel || {};
  const stage0 = funnel.stage_0_input || 1;

  return (
    <div data-testid="admin-dealflow-debug-page" className="space-y-6">
      <header>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/dealflow/{vertical}/debug
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium mt-1">DealFlow debug</h1>
      </header>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-zinc-500">Vertical</span>
          <select
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            data-testid="debug-vertical"
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200"
          >
            <option value="auto">auto</option>
            <option value="immo">immo</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs md:col-span-2">
          <span className="text-zinc-500">User ULID (blank = current admin)</span>
          <input
            type="text"
            value={userUlid}
            onChange={(e) => setUserUlid(e.target.value.trim())}
            data-testid="debug-user-ulid"
            placeholder={user?.ulid || ""}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200 font-mono text-xs"
          />
        </label>
        <div className="flex flex-col gap-1 text-xs">
          <span className="text-zinc-500">Image health toggles</span>
          <div className="flex gap-3 py-1.5">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={includeDead}
                onChange={(e) => setIncludeDead(e.target.checked)}
                data-testid="debug-include-dead"
              />
              dead
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={includeUnchecked}
                onChange={(e) => setIncludeUnchecked(e.target.checked)}
                data-testid="debug-include-unchecked"
              />
              unchecked
            </label>
          </div>
        </div>
        <div className="md:col-span-4">
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            data-testid="debug-refresh-btn"
            className="px-3 py-1.5 text-sm rounded-md bg-emerald-500 text-zinc-950 font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Loading debug snapshot…</div>
      ) : isError ? (
        <div className="text-rose-400">{String(error?.message || error)}</div>
      ) : !data ? null : (
        <>
          <Section title="Effective preferences" testid="debug-effective-prefs">
            <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-all bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 max-h-64 overflow-auto">
              {JSON.stringify(data.effective_prefs || {}, null, 2)}
            </pre>
          </Section>

          <Section title="Funnel" testid="debug-funnel">
            <div className="space-y-1.5">
              {FUNNEL_KEYS.map((k) => (
                <FunnelBar key={k} label={k} value={funnel[k] || 0} max={stage0} />
              ))}
            </div>
          </Section>

          <Section title="Exclusions" testid="debug-exclusions">
            <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-right px-3 py-2">Count</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  {EXCLUSION_KEYS.map((k) => (
                    <tr key={k} className="border-t border-zinc-900/60">
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{k}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {data.exclusions?.[k] ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {data.samples?.excluded_ids?.length > 0 && (
            <Section title={`Sample exclusions (${data.samples.excluded_ids.length})`} testid="debug-samples">
              <div className="space-y-1.5 max-h-80 overflow-auto">
                {data.samples.excluded_ids.map((s, i) => (
                  <div key={i} className="rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-[11px] font-mono">
                    <span className="text-zinc-300">{s.ulid?.slice(0, 12)}…</span>
                    <span className="ml-2 text-rose-300">{s.reason}</span>
                    {s.value != null && (
                      <span className="ml-2 text-zinc-400">
                        value={JSON.stringify(s.value)}
                      </span>
                    )}
                    {s.wanted != null && (
                      <span className="ml-2 text-zinc-500">
                        wanted={JSON.stringify(s.wanted)}
                      </span>
                    )}
                    {s.limit != null && (
                      <span className="ml-2 text-zinc-500">
                        limit={JSON.stringify(s.limit)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.breakdowns?.by_source && (
              <Section title="By source" testid="debug-by-source">
                <BarList items={
                  Object.entries(data.breakdowns.by_source).map(([label, value]) => ({ label, value }))
                } />
              </Section>
            )}
            {data.breakdowns?.by_country && (
              <Section title="By country" testid="debug-by-country">
                <BarList items={
                  Object.entries(data.breakdowns.by_country).map(([label, value]) => ({ label, value }))
                } />
              </Section>
            )}
            {data.breakdowns?.by_image_health && (
              <Section title="By image health" testid="debug-by-image-health">
                <BarList items={
                  Object.entries(data.breakdowns.by_image_health).map(([label, value]) => ({ label, value }))
                } />
              </Section>
            )}
          </div>

          {data.timing_ms && (
            <Section title="Timing (ms)">
              <ul className="text-xs font-mono text-zinc-400 space-y-0.5">
                {Object.entries(data.timing_ms).map(([k, v]) => (
                  <li key={k}>
                    <span className="text-zinc-500">{k}:</span>{" "}
                    <span className="text-zinc-200 tabular-nums">{v} ms</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <div className="text-[10px] text-zinc-600 font-mono">
            computed_at: {fmtDate(data.computed_at || data.meta?.computed_at)}
          </div>
        </>
      )}
    </div>
  );
}
